import { ReactNode, useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export interface Column<T> {
  header: string;
  render: (row: T) => ReactNode;
  /** Real, opt-in client-side sorting - the raw value to sort by (never the rendered ReactNode). Omit for columns that genuinely aren't sortable (e.g. action buttons). */
  sortValue?: (row: T) => string | number;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  emptyMessage?: string;
  /** Real, server-side pagination info - when provided, renders a Previous/Next footer that calls back to the parent to refetch. Never a client-side illusion hiding already-fetched rows. */
  pagination?: PaginationInfo;
}

/**
 * The shared VConnect table - genuine client-side sorting (safe,
 * since it only reorders rows already fetched, never hides real
 * data) plus optional real server-side pagination (the parent owns
 * the actual page state and refetch, this component only renders
 * the controls and reports clicks back up).
 */
export function DataTable<T>({ columns, rows, rowKey, emptyMessage = 'No records yet.', pagination }: DataTableProps<T>) {
  const [sortState, setSortState] = useState<{ header: string; direction: 'asc' | 'desc' } | null>(null);

  const sortedRows = useMemo(() => {
    if (!sortState) return rows;
    const column = columns.find((c) => c.header === sortState.header);
    if (!column?.sortValue) return rows;
    const sorted = [...rows].sort((a, b) => {
      const va = column.sortValue!(a);
      const vb = column.sortValue!(b);
      if (va < vb) return -1;
      if (va > vb) return 1;
      return 0;
    });
    return sortState.direction === 'desc' ? sorted.reverse() : sorted;
  }, [rows, sortState, columns]);

  const toggleSort = (header: string) => {
    setSortState((prev) => {
      if (prev?.header !== header) return { header, direction: 'asc' };
      if (prev.direction === 'asc') return { header, direction: 'desc' };
      return null;
    });
  };

  if (rows.length === 0) {
    return <div className="empty-state">{emptyMessage}</div>;
  }

  return (
    <div className="data-table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.header}>
                {col.sortValue ? (
                  <button type="button" className="data-table-sort-header" onClick={() => toggleSort(col.header)}>
                    {col.header}
                    {sortState?.header === col.header && (sortState.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </button>
                ) : col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((col) => (
                <td key={col.header}>{col.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <div className="data-table-pagination">
          <span className="data-table-pagination-info">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
          </span>
          <div className="data-table-pagination-controls">
            <button type="button" className="btn-secondary" disabled={pagination.page <= 1} onClick={() => pagination.onPageChange(pagination.page - 1)}>
              <ChevronLeft size={14} /> Previous
            </button>
            <button type="button" className="btn-secondary" disabled={pagination.page >= pagination.totalPages} onClick={() => pagination.onPageChange(pagination.page + 1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
